import {DI_TOKENS} from '@/di/di.tokens';

import {Container} from 'brandi';

import {initLocalStorage} from '@specials/localstorage';
import {SentryBrowser} from '@specials/sentry-browser';

import {apiRoutes, clientRoutes} from '@/constants';
import {Environment, envs} from '@/envs';
import {ResourcesAll} from '@/resources';
import {CacheService} from '@/services/CacheService/CacheService';
import {CacheLocalStorage} from '@/services/CacheService/CacheStorage/CacheLocalStorage';
import {DateTimeService} from '@/services/DateTimeService/DateTimeService';
import {DevToolsMbService} from '@/services/DevToolsMbService/DevToolsMbService';
import {ErrorService} from '@/services/ErrorService/ErrorService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {NavigationService} from '@/services/NavigationService/NavigationService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {HttpClientProjectApi} from '@/services/ProjectApi/ProjectApiHttpClient';
import {QueryStringService} from '@/services/QueryStringService/QueryStringService';
import {ScrollService} from '@/services/ScrollService/ScrollService';
import {ShareService} from '@/services/ShareService/ShareService';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {WindowVisibilityService} from '@/services/WindowVisibilityService/WindowVisibilityService';
import {ModalStore} from '@/stores/ModalStore/ModalStore';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {Envs, GlobalWindow} from '@/types';
import {ErrorActionHandlerUseCase} from '@/useCases/ErrorActionHandlerUseCase/ErrorActionHandlerUseCase';
import {InitializeUseCase} from '@/useCases/InitializeUseCase/InitializeUseCase';
import {ShareUseCase} from '@/useCases/ShareUseCase/ShareUseCase';
import {ModalControlSharedUseCase} from '@/useCases/shared/ModalControlSharedUseCase/ModalControlSharedUseCase';
import {CookiesService} from "@/services/CookiesService/CookiesService";

export function initInfraDeps(container: Container, resources: ResourcesAll): void {
  container.bind(DI_TOKENS.envs).toConstant(envs);
  container.bind(DI_TOKENS.resources).toConstant(resources);
  container.bind(DI_TOKENS.clientRoutes).toConstant(clientRoutes);
  container.bind(DI_TOKENS.apiRoutes).toConstant(apiRoutes);
  container.bind(DI_TOKENS.globalWindow).toConstant(globalThis.window);

  container
    .bind(DI_TOKENS.config)
    .toConstant(configFactory(container.get(DI_TOKENS.envs), container.get(DI_TOKENS.globalWindow)));

  container.bind(DI_TOKENS.httpClientProjectApi).toInstance(HttpClientProjectApi).inSingletonScope();
  container.bind(DI_TOKENS.scrollService).toInstance(ScrollService).inSingletonScope();

  container
    .bind(DI_TOKENS.sentryBrowser)
    .toInstance(() => SentryBrowser.getRealOrFake(container.get(DI_TOKENS.globalWindow)))
    .inSingletonScope();
  container.bind(DI_TOKENS.loggerService).toInstance(LoggerService).inSingletonScope();
  container.bind(DI_TOKENS.queryStringService).toInstance(QueryStringService).inSingletonScope();
  container.bind(DI_TOKENS.platformService).toInstance(PlatformService).inSingletonScope();

  container.bind(DI_TOKENS.trackingService).toInstance(TrackingService).inSingletonScope();
  container.bind(DI_TOKENS.cookiesService).toInstance(CookiesService).inSingletonScope();

  container
    .bind(DI_TOKENS.localStorageService)
    .toInstance(() => initLocalStorage())
    .inSingletonScope();
  container.bind(DI_TOKENS.navigationService).toInstance(NavigationService).inSingletonScope();

  container.bind(DI_TOKENS.windowVisibilityService).toInstance(WindowVisibilityService).inSingletonScope();
  container.bind(DI_TOKENS.devtoolsMbService).toInstance(DevToolsMbService).inSingletonScope();
  container.bind(DI_TOKENS.errorService).toInstance(ErrorService).inSingletonScope();
  container.bind(DI_TOKENS.shareStore).toInstance(ShareStore).inSingletonScope();
  container.bind(DI_TOKENS.shareService).toInstance(ShareService).inSingletonScope();
  container.bind(DI_TOKENS.dateTimeService).toInstance(DateTimeService).inSingletonScope();
  container.bind(DI_TOKENS.cacheStorage).toInstance(CacheLocalStorage).inSingletonScope();
  container.bind(DI_TOKENS.cacheService).toInstance(CacheService).inSingletonScope();
  container.bind(DI_TOKENS.modalStore).toInstance(ModalStore).inSingletonScope();
  container.bind(DI_TOKENS.initializeUseCase).toInstance(InitializeUseCase).inSingletonScope();
  container.bind(DI_TOKENS.shareUseCase).toInstance(ShareUseCase).inSingletonScope();
  container.bind(DI_TOKENS.modalControlSharedUseCase).toInstance(ModalControlSharedUseCase).inSingletonScope();
  container.bind(DI_TOKENS.errorActionHandlerUseCase).toInstance(ErrorActionHandlerUseCase).inSingletonScope();
}

/* Глушим eslint, потому что испольузем ReturnType<configFactory> в качестве типа для конфига в DI */
/* eslint-disable-next-line */
export function configFactory(envsData: Envs, globalWindow: GlobalWindow) {
  return {
    isLocal: envsData.APP_ENVIRONMENT === Environment.Local,
    isStage: envsData.APP_ENVIRONMENT === Environment.Stage,
    isProd: envsData.APP_ENVIRONMENT === Environment.Prod,
  };
}
